import { TextConstraintConfig } from './inputConstraints';

function normalizeWhitespace(value: string, allowNewlines: boolean): string {
  let normalized = value;

  if (allowNewlines) {
    normalized = normalized.replace(/\r\n?/g, '\n');
    normalized = normalized.replace(/[\t\v\f]+/g, ' ');
    normalized = normalized
      .split('\n')
      .map((line) => line.replace(/ {2,}/g, ' '))
      .join('\n');
  } else {
    normalized = normalized.replace(/[\s\u00A0]+/g, ' ');
  }

  return normalized;
}

function containsControlCharacters(value: string, allowNewlines: boolean): boolean {
  const pattern = allowNewlines
    ? /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/
    : /[\u0000-\u001F\u007F]/;
  return pattern.test(value);
}

export interface ConstraintValidationOptions {
  requiredOverride?: boolean;
}

export interface ConstraintValidationResult {
  valid: boolean;
  value: string | null;
  wasTransformed: boolean;
  error?: string;
}

export function validateTextWithConstraint(
  rawValue: unknown,
  constraint: TextConstraintConfig,
  options?: ConstraintValidationOptions
): ConstraintValidationResult {
  const required = options?.requiredOverride ?? constraint.required ?? false;

  if (rawValue === undefined || rawValue === null) {
    if (required) {
      return { valid: false, value: null, wasTransformed: false, error: `${constraint.label} is required` };
    }
    return { valid: true, value: null, wasTransformed: false };
  }

  if (typeof rawValue !== 'string') {
    return { valid: false, value: null, wasTransformed: false, error: `${constraint.label} must be a string` };
  }

  let value = rawValue;
  let wasTransformed = false;

  if (constraint.trim !== false) {
    const trimmed = value.trim();
    if (trimmed !== value) {
      wasTransformed = true;
      value = trimmed;
    }
  }

  if (!required && value.length === 0) {
    return { valid: true, value: null, wasTransformed };
  }

  if (constraint.normalizeWhitespace) {
    const normalized = normalizeWhitespace(value, Boolean(constraint.allowNewlines));
    if (normalized !== value) {
      wasTransformed = true;
      value = normalized;
    }
  }

  if (!constraint.allowNewlines && /[\r\n]/.test(value)) {
    return { valid: false, value: null, wasTransformed, error: `${constraint.label} cannot contain line breaks` };
  }

  if (constraint.transform === 'lowercase') {
    const lower = value.toLowerCase();
    if (lower !== value) {
      wasTransformed = true;
      value = lower;
    }
  } else if (constraint.transform === 'uppercase') {
    const upper = value.toUpperCase();
    if (upper !== value) {
      wasTransformed = true;
      value = upper;
    }
  }

  if (constraint.disallowControlChars && containsControlCharacters(value, Boolean(constraint.allowNewlines))) {
    return { valid: false, value: null, wasTransformed, error: `${constraint.label} contains invalid control characters` };
  }

  const minLength = constraint.minLength ?? 0;
  const maxLength = constraint.maxLength ?? 0;

  if (minLength > 0 && value.length < minLength) {
    return { valid: false, value: null, wasTransformed, error: `${constraint.label} must be at least ${minLength} characters` };
  }

  if (maxLength > 0 && value.length > maxLength) {
    return { valid: false, value: null, wasTransformed, error: `${constraint.label} must be at most ${maxLength} characters` };
  }

  if (constraint.pattern) {
    const pattern = new RegExp(constraint.pattern.source, constraint.pattern.flags);
    if (!pattern.test(value)) {
      const suffix = constraint.patternDescription ? ` (${constraint.patternDescription})` : '';
      return { valid: false, value: null, wasTransformed, error: `${constraint.label} contains invalid characters${suffix}` };
    }
  }

  if (Array.isArray(constraint.blocklist)) {
    for (const entry of constraint.blocklist) {
      const regex = new RegExp(entry.source, entry.flags);
      if (regex.test(value)) {
        return { valid: false, value: null, wasTransformed, error: `${constraint.label} contains a forbidden pattern` };
      }
    }
  }

  return { valid: true, value, wasTransformed };
}
